<template>
  <AmToast
    v-if="toastFeedback"
    :message="toastMessage"
    :duration="3000"
    margin="mt-28 ml-4"
    position="top-right"
    closable
  />

  <Teleport
    v-if="isAdmin && returnBarReady && mq.current === 'desktop'"
    to="#am-return-bar-actions"
  >
    <div class="my-apps__search">
      <AmInput
        v-model="search"
        class="my-apps__search-input"
        size="fullWidth"
        :placeholder="$t('myApps.header.searchApp')"
        dataTestName="my-apps-search"
      />
      <AmButton
        v-if="search"
        variant="maxGhost"
        :icon="{ name: 'close', size: 'small' }"
        size="iconSmall"
        class="my-apps__search-clear"
        dataTestName="my-apps-search-clear"
        @click="search = ''"
      />
    </div>
  </Teleport>

  <div
    class="my-apps"
    :class="{
      'my-apps--mobile': mq.current === 'mobile',
      'my-apps--desktop': mq.current !== 'mobile'
    }"
  >
    <AmLoading
      v-if="loading"
      :isLoading="loading"
      class="m-auto"
    />
    <div
      v-else
      class="my-apps__content"
    >
      <AmEmptyBackground
        v-if="apps.length === 0"
        icon="folder"
        :text="$t('myApps.emptyState.title')"
        :action="$t('myApps.emptyState.action')"
        sizeAction="xlarge"
        @clickAction="router.push({ name: 'create-app' })"
      />
      <DataTable
        v-else
        class="my-apps__table"
        :value="filteredApps"
        dataKey="id"
        rowHover
        stripedRows
        responsiveLayout="stack"
        breakpoint="767px"
        :paginator="isAdmin"
        :rows="10"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
        :currentPageReportTemplate="paginationReport"
        @row-click="onRowClick"
      >
        <template #empty>
          <div class="my-apps__empty-search">
            {{ $t('myApps.header.searchNoResults') }}
          </div>
        </template>
        <Column
          field="name"
          :header="$t('myApps.header.table.name')"
          :style="{ width: '35%' }"
          bodyClass="my-apps__cell my-apps__cell--name"
        >
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.name') }}</span>
            <span
              class="my-apps__row-link"
              :data-test="`row-name-${data.id}`"
              role="link"
              tabindex="0"
              @click.stop="goToApp(data.id)"
              @keydown.enter.stop="goToApp(data.id)"
            >
              {{ data.name }}
            </span>
          </template>
        </Column>

        <Column
          v-if="isAdmin"
          field="is_private"
          :header="$t('myApps.header.table.visibility')"
          bodyClass="my-apps__cell"
        >
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.visibility') }}</span>
            <AmChip
              :text="data.is_private ? $t('myApps.visibility.private') : $t('myApps.visibility.public')"
              :type="data.is_private ? 'lightGray' : 'success'"
            />
          </template>
        </Column>

        <Column
          v-if="isAdmin"
          field="user.company_name"
          :header="$t('myApps.header.table.owner')"
          bodyClass="my-apps__cell my-apps__cell--owner"
        >
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.owner') }}</span>
            <div class="my-apps__owner">
              <span
                class="my-apps__owner-name"
                :data-test="`row-owner-${data.id}`"
              >{{ ownerName(data) }}</span>
              <span
                v-if="data.user?.email"
                class="my-apps__owner-email"
              >{{ data.user.email }}</span>
            </div>
          </template>
        </Column>

        <Column
          field="created_at"
          :header="$t('myApps.header.table.createdAt')"
          bodyClass="my-apps__cell"
        >
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.createdAt') }}</span>
            <AmTypography
              :text="date(data.created_at, 'fullDate')"
              typographyStyle="l6"
              textColor="text60"
            />
          </template>
        </Column>

        <Column
          v-if="!isAdmin"
          field="is_private"
          :header="$t('myApps.header.table.isPrivate')"
          bodyClass="my-apps__cell"
        >
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.isPrivate') }}</span>
            <AmButton
              v-if="data.is_private"
              :label="$t('myApps.actions.share')"
              variant="maxGhost"
              :icon="{
                name: 'share',
                size: 'small',
                position: 'withLeftIcon'
              }"
              @click.stop="copyURL(data.access_url)"
            />
            <AmTypography
              v-else
              text="-"
              typographyStyle="l6"
              textColor="text120"
            />
          </template>
        </Column>

        <Column
          field="status_name"
          bodyClass="my-apps__cell"
        >
          <template #header>
            <span class="flex items-center gap-1">
              {{ $t('myApps.header.table.status') }}
              <button
                type="button"
                class="my-apps__status-helper"
                aria-label="Ver status disponíveis"
                @click.stop="openStatusModal"
              >
                <span class="material-icons text-base">help_outline</span>
              </button>
            </span>
          </template>
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.status') }}</span>
            <AmChip
              :text="status(data.status_name)?.tag ? $t(`myApps.status.${status(data.status_name).tag}`) : data.status_name"
              :data-test="`status-tag-${status(data.status_name)?.tag || 'unknown'}`"
              :type="status(data.status_name)?.type || 'secondary'"
              isStatusTag
            />
          </template>
        </Column>

        <Column
          :header="$t('myApps.header.table.actions')"
          :style="{ width: '80px' }"
          bodyClass="my-apps__cell my-apps__actions-cell"
        >
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.actions') }}</span>
            <div @click.stop>
              <!-- Configurando (in-draft + development unificados) -->
              <AmDropdown
                v-if="status(data.status_name)?.tag === 'configuring'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-configuring"
                :options="[
                  ...adminCredentialActions(data.id),
                  {
                    title: $t('myApps.actions.configuring'),
                    value: `configuring ${data.id}`,
                    action: () => handleAction('configuring', data.id, data)
                  }
                ]"
              />

              <!-- Em teste -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'inTest'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-intest"
                :options="[
                  ...adminCredentialActions(data.id),
                  {
                    title: $t('myApps.actions.development'),
                    value: `development ${data.id}`,
                    action: () => handleAction('development', data.id, data)
                  },
                  {
                    title: $t('myApps.actions.credentials'),
                    value: `credentials ${data.id}`,
                    action: () => handleAction('credentials', data.id, data)
                  }
                ]"
              />

              <!-- Em análise -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'inAnalysis'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-inanalysis"
                :options="[
                  ...adminCredentialActions(data.id),
                  ...(isAdmin ? [{
                    title: $t('myApps.actions.moveToHomolog'),
                    value: `move-homolog ${data.id}`,
                    action: () => handleAdminMoveToHomolog(data.id)
                  }] : []),
                  ...(isAdmin && data.is_private ? [{
                    title: $t('myApps.actions.publishPrivate'),
                    value: `publish-private ${data.id}`,
                    action: () => handleAdminPublishPrivate(data.id)
                  }] : []),
                  {
                    title: $t('myApps.actions.credentials'),
                    value: `credentials ${data.id}`,
                    action: () => handleAction('credentials', data.id, data)
                  }
                ]"
              />

              <!-- Publicado -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'published'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-published"
                :options="[
                  ...adminCredentialActions(data.id),
                  {
                    title: $t('myApps.actions.edit'),
                    value: `edit ${data.id}`,
                    action: () => handleAction('edit', data.id)
                  },
                  {
                    title: $t('myApps.actions.deactivate'),
                    value: `deactivate ${data.id}`,
                    action: () => handleAction('deactivate', data.id)
                  },
                  {
                    title: $t('myApps.actions.credentials'),
                    value: `credentials ${data.id}`,
                    action: () => handleAction('credentials', data.id, data)
                  }
                ]"
              />

              <!-- Em homologação -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'homolog'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-homolog"
                :options="[
                  ...adminCredentialActions(data.id),
                  ...(isAdmin ? [{
                    title: $t('myApps.actions.approve'),
                    value: `approve ${data.id}`,
                    action: () => handleAdminApprove(data.id)
                  }] : []),
                  {
                    title: $t('myApps.actions.credentials'),
                    value: `credentials ${data.id}`,
                    action: () => handleAction('credentials', data.id, data)
                  }
                ]"
              />

              <!-- Ajuste pendente -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'adjustmentPending'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-adjustment"
                :options="[
                  ...adminCredentialActions(data.id),
                  {
                    title: $t('myApps.actions.adjustment'),
                    value: `adjustment ${data.id}`,
                    action: () => handleAction('adjustment', data.id)
                  }
                ]"
              />

              <!-- Reprovado -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'reproved'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-reproved"
                :options="[
                  ...adminCredentialActions(data.id),
                  {
                    title: $t('myApps.actions.deactivate'),
                    value: `deactivate ${data.id}`,
                    action: () => handleAction('deactivate', data.id)
                  }
                ]"
              />

              <!-- Desativado -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'deactivate'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-deactivate"
                :options="[
                  ...adminCredentialActions(data.id),
                  {
                    title: $t('myApps.actions.reactivate'),
                    value: `reactivate ${data.id}`,
                    action: () => handleAction('reactivate', data.id)
                  }
                ]"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>

  <AmModal
    v-model="showModal"
    maxWidth="500px"
    :title="$t(`amDeactivateAppModal.title`)"
    class="app-config-finished-modal"
    :persistent="true"
    @close="showModal = false"
  >
    <div :class="[`deactivate-app-${mq.current}`]">
      <AmTypography
        :text="$t(`amDeactivateAppModal.content`)"
        enfaseTextColor="black"
        enfaseText="desenvolvedores@appmax.com.br "
        :nextText="$t('amDeactivateAppModal.nextContent')"
        class="home-view__left__infos__subtitle--label"
      />
      <AmButton
        :label="$t('amDeactivateAppModal.understand')"
        variant="maxPrimary"
        class="float-right app-config-finished-modal__button"
        @click="showModal = false"
      />
    </div>
  </AmModal>

  <AmModal
    v-model="showModalReactivate"
    :title="$t(`amReactivateAppModal.title`)"
    maxWidth="500px"
    :persistent="true"
    @close="closeModalReactivate"
  >
    <AmReactivateAppModal @understandModalReactivate="showModalReactivate = false" />
  </AmModal>

  <AmModal
    v-model="showStatusModal"
    title="Status"
    maxWidth="800px"
    :persistent="true"
    @close="closeStatusModal"
  >
    <AmStatusModal />
  </AmModal>

  <AmModal
    v-model="showModalCredentials"
    maxWidth="500px"
    :title="$t('myApps.credentialsModal.title')"
    :persistent="true"
    @close="closeCredentialsModal"
  >
    <AmLoading
      v-if="loadingCredentials"
      :isLoading="loadingCredentials"
      class="mx-auto pb-10"
    />
    <div
      v-else
      :class="[`app-config-finished-modal-${mq.current}`]"
    >
      <div class="app-config-finished-modal">
        <div
          v-if="appCredentials.error"
          class="mb-4 p-3 bg-red-100 text-red-700 rounded"
        >
          <strong>Erro:</strong> {{ appCredentials.error }}
        </div>

        <div
          v-else-if="hasValidCredentials"
          class="app-config-finished-modal__top-content"
        >
          <AmTypography
            :text="$t('myApps.credentialsModal.appId')"
            typographyStyle="l5"
            weight="medium"
            class="mt-4"
          />

          <AppCopyInput
            v-if="appCredentials.appId"
            :modelValue="appCredentials.appId"
            readOnly
            size="fullWidth"
            class="mb-3"
          />

          <div
            v-if="appCredentials.sandbox && appCredentials.sandbox.client_id"
            class="credential-divider"
          >
            <AmTypography
              :text="$t('myApps.credentialsModal.sandbox')"
              typographyStyle="l6"
              weight="bold"
              textColor="700"
              class="mb-3"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.sandboxDescription')"
              typographyStyle="l7"
              class="mb-3"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.clientId')"
              typographyStyle="l6"
              weight="medium"
            />

            <AppCopyInput
              v-if="appCredentials.sandbox.client_id"
              :modelValue="appCredentials.sandbox.client_id"
              readOnly
              size="fullWidth"
              class="mb-4"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.clientSecret')"
              typographyStyle="l6"
              weight="medium"
            />

            <CredentialSecret
              :appId="appCredentials.id"
              stage="homolog"
              :revealed="!!appCredentials.sandbox.revealed"
              class="mb-2"
              @refresh="refreshCredentials"
            />

            <div
              v-if="appCredentials.sandbox.email || appCredentials.sandbox.password"
              class="credential-divider mt-4"
            >
              <AmTypography
                :text="$t('myApps.credentialsModal.loginTitle')"
                typographyStyle="l6"
                weight="bold"
                textColor="700"
                class="mb-3"
              />

              <AmTypography
                :text="$t('myApps.credentialsModal.loginDescription', { url: env.app.breakingCodeURL })"
                typographyStyle="l7"
                class="mb-3"
              />

              <AmTypography
                :text="$t('myApps.credentialsModal.loginEmail')"
                typographyStyle="l6"
                weight="medium"
              />

              <AppCopyInput
                v-if="appCredentials.sandbox.email"
                :modelValue="appCredentials.sandbox.email"
                readOnly
                size="fullWidth"
                class="mb-4"
              />

              <AmTypography
                :text="$t('myApps.credentialsModal.loginPassword')"
                typographyStyle="l6"
                weight="medium"
              />

              <AppCopyInput
                v-if="appCredentials.sandbox.password"
                :modelValue="appCredentials.sandbox.password"
                readOnly
                size="fullWidth"
                class="mb-2"
              />
            </div>
          </div>

          <div
            v-if="
              !appCredentials.showOnlySandbox &&
                appCredentials.homolog &&
                appCredentials.homolog.client_id
            "
          >
            <AmTypography
              :text="$t('myApps.credentialsModal.production')"
              typographyStyle="l6"
              weight="bold"
              class="mb-3"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.productionDescription')"
              typographyStyle="l7"
              class="mb-4"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.clientId')"
              typographyStyle="l6"
              weight="medium"
            />

            <AppCopyInput
              v-if="appCredentials.homolog.client_id"
              :modelValue="appCredentials.homolog.client_id"
              readOnly
              size="fullWidth"
              class="mb-4"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.clientSecret')"
              typographyStyle="l6"
              weight="medium"
            />

            <CredentialSecret
              :appId="appCredentials.id"
              stage="production"
              :revealed="!!appCredentials.homolog.revealed"
              class="mb-8"
              @refresh="refreshCredentials"
            />
          </div>
        </div>

        <div
          v-else-if="!appCredentials.error"
          class="mb-4 p-3 bg-yellow-100 text-yellow-700 rounded"
        >
          <strong>Aviso:</strong> Nenhuma credencial encontrada para este aplicativo.
        </div>

        <div class="app-config-finished-modal__bottom-content__button mt-6 flex justify-end">
          <AmButton
            :label="$t('myApps.credentialsModal.action')"
            variant="maxPrimary"
            @click="closeCredentialsModal"
          />
        </div>
      </div>
    </div>
  </AmModal>
</template>

<script setup>
import DataTable from '@appmax_npm/ds-prime/datatable'
import Column from '@appmax_npm/ds-prime/column'
import {
  AmButton,
  AmLoading,
  AmChip,
  AmTypography,
  AmDropdown,
  AmModal,
  AmEmptyBackground,
  AmToast,
  AmInput
} from '@appmax_npm/ds-appmax-v3'
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import './MyApps.scss'
import AmStatusModal from '../../molecules/AmStatusModal/AmStatusModal.vue'
import AmReactivateAppModal from '../../molecules/AmReactivateAppModal/AmReactivateAppModal.vue'
import CredentialSecret from '../../molecules/CredentialSecret/CredentialSecret.vue'
import AppCopyInput from '../../atoms/AppCopyInput/AppCopyInput.vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHttpStore } from '../../../stores/http'
import { useAppStore, ScreenMode } from '@/stores/modules/app'
import { useAuthStore } from '@/stores/auth'
import { getStatusBadgeProps, normalizeStatusKey } from '@/utils/appStatus'

const mq = inject('mq')
const loading = ref(false)
const router = useRouter()
const httpStore = useHttpStore()
const appStore = useAppStore()
const authStore = useAuthStore()
const { date } = inject('formatter')
const { t, locale } = useI18n()
const env = inject('env')

// Coluna de dono é exibida apenas para admins (listagem geral de apps).
const isAdmin = computed(() => authStore.isAdmin)
const ownerName = (data) => data?.user?.company_name || data?.user?.social_reason || '—'

const paginationReport = computed(() =>
  locale.value?.startsWith('en')
    ? '{first}–{last} of {totalRecords}'
    : '{first}–{last} de {totalRecords}'
)

const apps = ref([])

// Busca por nome (visão admin) — filtro client-side sobre a lista já carregada.
const search = ref('')
const filteredApps = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return apps.value
  return apps.value.filter((app) => (app.name || '').toLowerCase().includes(term))
})

// O campo de busca é teleportado para a return bar (mesma linha do "Voltar").
// A return bar é montada no App.vue como async component, podendo aparecer no
// DOM depois deste componente — sinalizamos quando o alvo (#am-return-bar-actions)
// existe. Usamos o seletor string no Teleport para o Vue re-resolver o alvo a
// cada patch, evitando operar sobre nó órfão durante a navegação.
const returnBarReady = ref(false)
let returnBarObserver = null

onMounted(() => {
  if (document.getElementById('am-return-bar-actions')) {
    returnBarReady.value = true
    return
  }
  returnBarObserver = new MutationObserver(() => {
    if (document.getElementById('am-return-bar-actions')) {
      returnBarReady.value = true
      returnBarObserver.disconnect()
      returnBarObserver = null
    }
  })
  returnBarObserver.observe(document.body, { childList: true, subtree: true })
})

onUnmounted(() => {
  if (returnBarObserver) {
    returnBarObserver.disconnect()
    returnBarObserver = null
  }
  returnBarReady.value = false
})

const toastFeedback = ref(false)
const toastMessage = ref(null)
const action = ref('')
const showModal = ref(false)
const showModalReactivate = ref(false)
const showModalCredentials = ref(false)
const showStatusModal = ref(false)
// AppDeveloperModal removido: acao 'development' navega para /apps/:id (AppDetailsView)
const appCredentials = ref({})
const credentialsAppItem = ref(null)
const loadingCredentials = ref(false)

const hasValidCredentials = computed(() => {
  const sandboxValid = appCredentials.value.sandbox && appCredentials.value.sandbox.client_id

  const homologValid =
    !appCredentials.value.showOnlySandbox &&
    appCredentials.value.homolog &&
    appCredentials.value.homolog.client_id

  const hasAppId = appCredentials.value.appId

  return (sandboxValid || homologValid) && hasAppId
})

const closeStatusModal = () => {
  showStatusModal.value = false
}

const closeModalReactivate = () => {
  showModalReactivate.value = false
}

const closeCredentialsModal = () => {
  showModalCredentials.value = false
  appCredentials.value = {}
  credentialsAppItem.value = null
}

// Recarrega o status das credenciais (GET) apos revelar/regerar um secret
const refreshCredentials = () => {
  if (!appCredentials.value.id) return
  getAppCredentials(appCredentials.value.id, credentialsAppItem.value)
}


const goToApp = (id) => {
  if (!id) return
  router.push({ name: 'app', params: { id: id.toString() } }).catch(() => {})
}

const onRowClick = ({ data, originalEvent }) => {
  // Não navega quando o clique foi numa ação da linha (botões, dropdown, etc.)
  if (originalEvent?.target?.closest('button, a, input, [role="button"], .am-dropdown')) {
    return
  }
  goToApp(data?.id)
}

const copyURL = (url) => {
  navigator.clipboard.writeText(`${env.app.systemURL}/${url}`)

  toastMessage.value = {
    alertType: 'success',
    title: t('myApps.toast.copyURL')
  }

  toastFeedback.value = true

  setTimeout(() => {
    toastFeedback.value = false
  }, 3000)
}

// Mapa de ações por tag canônico — determina qual CTA aparece no dropdown/link
const ACTION_MAP = {
  configuring:        { label: 'configuring' },
  inTest:             { label: 'development' },
  inAnalysis:         { label: 'edit' },
  adjustmentPending:  { label: 'adjustment' },
  homolog:            { label: 'credentials' },
  published:          null,
  reproved:           { label: 'deactivate' },
  deactivate:         { label: 'reactivate' }
}

/**
 * Retorna as props do badge + ação para um statusName vindo do backend.
 * Usa getStatusBadgeProps() do utils centralizado como fonte única.
 */
const status = (statusName) => {
  const props = getStatusBadgeProps(statusName)

  if (!props) {
    return { tag: 'unknown', type: 'secondary', action: null }
  }

  return {
    ...props,
    action: ACTION_MAP[props.tag] ?? null
  }
}

const handleAction = (actionLabel, id, item = []) => {
  return {
    edit: () => router.push({ path: `/app/${id}/edit` }),
    // 'development' mantido para compatibilidade (inTest usa este label)
    development: () => {
      router.push({ name: 'app', params: { id: id.toString() } })
    },
    // 'configuring' — ação unificada para in-draft e development
    configuring: () => {
      if (item?.status_name && normalizeStatusKey(item.status_name) === 'in-draft') {
        router.push({ name: 'create-app' })
        appStore.screenMode = ScreenMode.DRAFT
        appStore.isSelectedApp = true
        appStore.app.id = id
      } else {
        router.push({ name: 'app', params: { id: id.toString() } })
      }
    },
    deactivate: () => {
      deactivateAppModal()
    },
    reactivate: () => {
      reactivateAppModal()
    },
    credentials: () => {
      getAppCredentials(id, item)
    },
    inDraft: () => {
      router.push({ name: 'create-app' })
      appStore.screenMode = ScreenMode.DRAFT
      appStore.isSelectedApp = true
      appStore.app.id = id
    },
    adjustment: () => {
      router.push({ path: `/app/${id}/edit` })
    }
  }[actionLabel]()
}

const openStatusModal = () => {
  showStatusModal.value = true
}

const getApps = async () => {
  loading.value = true

  httpStore.getApps().then((response) => {
    const {
      data: { data }
    } = response

    apps.value = data || []

    loading.value = false
  })
}

// --- Ações rápidas de gestão (admin) ---
const showToast = (alertType, title) => {
  toastMessage.value = { alertType, title }
  toastFeedback.value = true
  setTimeout(() => {
    toastFeedback.value = false
  }, 3000)
}

const handleAdminMoveToHomolog = async (id) => {
  try {
    await httpStore.adminMoveAppToHomolog(id)
    await getApps()
    showToast('success', t('myApps.toast.movedToHomolog'))
  } catch (e) {
    showToast('error', e?.response?.data?.text || t('myApps.toast.actionError'))
  }
}

const handleAdminApprove = async (id) => {
  try {
    await httpStore.adminApproveApp(id)
    await getApps()
    showToast('success', t('myApps.toast.approved'))
  } catch (e) {
    showToast('error', e?.response?.data?.text || t('myApps.toast.actionError'))
  }
}

const handleAdminPublishPrivate = async (id) => {
  try {
    await httpStore.adminPublishPrivateApp(id)
    await getApps()
    showToast('success', t('myApps.toast.publishedPrivate'))
  } catch (e) {
    showToast('error', e?.response?.data?.text || t('myApps.toast.actionError'))
  }
}

const handleAdminGenerateSandboxCreds = async (id) => {
  try {
    await httpStore.adminGenerateSandboxCredentials(id)
    showToast('success', t('myApps.toast.sandboxCredsGenerated'))
  } catch (e) {
    showToast('error', e?.response?.data?.text || t('myApps.toast.actionError'))
  }
}

const handleAdminGenerateProductionCreds = async (id) => {
  try {
    await httpStore.adminGenerateProductionCredentials(id)
    showToast('success', t('myApps.toast.prodCredsGenerated'))
  } catch (e) {
    showToast('error', e?.response?.data?.text || t('myApps.toast.actionError'))
  }
}

// Opções admin para gerar credenciais (sandbox/produção). É espalhado em todos
// os dropdowns de ação da listagem; retorna [] para não-admins.
const adminCredentialActions = (id) =>
  isAdmin.value
    ? [
      {
        title: t('myApps.actions.generateSandboxCreds'),
        value: `gen-sandbox-creds ${id}`,
        action: () => handleAdminGenerateSandboxCreds(id)
      },
      {
        title: t('myApps.actions.generateProductionCreds'),
        value: `gen-prod-creds ${id}`,
        action: () => handleAdminGenerateProductionCreds(id)
      }
    ]
    : []


const deactivateAppModal = () => {
  if (!showModal.value) {
    showModal.value = !showModal.value
  } else {
    showModal.value = !showModal.value
    setTimeout(() => {
      showModal.value = !showModal.value
    }, 100)
  }
}

const reactivateAppModal = () => {
  if (!showModalReactivate.value) {
    showModalReactivate.value = !showModalReactivate.value
  } else {
    showModalReactivate.value = !showModalReactivate.value
    setTimeout(() => {
      showModalReactivate.value = !showModalReactivate.value
    }, 100)
  }
}

const getAppCredentials = async (appId, appItem = null) => {
  loadingCredentials.value = true
  // Guarda o item da linha para permitir recarregar o status apos revelar/regerar
  credentialsAppItem.value = appItem

  try {
    const response = await httpStore.getAppCredentials(appId)

    // Extrair os dados das credenciais corretamente
    const credentialsData = response?.data?.data?.stages || {}

    // Buscar o UUID do aplicativo na lista atual ou na resposta da API
    const currentApp = apps.value.find((app) => app.id === appId) || appItem
    const appUuid = currentApp?.uuid || response?.data?.data?.uuid || response?.data?.data?.app_uuid || response?.data?.data?.id || appId

    // Verificar o status do aplicativo para determinar quais credenciais mostrar
    const appStatusProps = status(currentApp?.status_name)
    const shouldShowOnlySandbox = appStatusProps?.tag === 'inAnalysis' || appStatusProps?.tag === 'inTest'

    // Backend retorna stages.homolog (dev/sandbox) e stages.production (real).
    // Mapeamos para os nomes usados na UI: sandbox e homolog (homolog = producao).
    appCredentials.value = {
      id: appId,
      appId: appUuid,
      sandbox: credentialsData.homolog || {},
      homolog: shouldShowOnlySandbox ? {} : credentialsData.production || {},
      showOnlySandbox: shouldShowOnlySandbox
    }

    showModalCredentials.value = true
  } catch (error) {
    const httpStatus = error?.response?.status
    appCredentials.value = (!httpStatus || httpStatus >= 500)
      ? { error: error.message, errorDetails: error }
      : {}
    showModalCredentials.value = true
  } finally {
    loadingCredentials.value = false
  }
}

onMounted(async () => {
  loading.value = true

  await getApps()

  // Fase 1.2: toast informativo após configuração concluída
  if (appStore.justConfiguredAppId) {
    toastMessage.value = {
      alertType: 'info',
      title: t('myApps.toast.configuredSummary'),
      description: t('myApps.toast.configuredDetail')
    }
    toastFeedback.value = true
    setTimeout(() => {
      toastFeedback.value = false
    }, 5000)
    appStore.justConfiguredAppId = null
  }
})
</script>
